import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { userHasAnyRole } from "../../../services/authService";
import {
  ChecklistPhase,
  ClinicalChecklistTemplate,
  createClinicalChecklistTemplate,
  deactivateClinicalChecklistTemplate,
  listClinicalChecklistTemplates,
  updateClinicalChecklistTemplate,
} from "../../../services/clinicalChecklistService";

type DraftStep = {
  title: string;
  description: string;
  phase: ChecklistPhase | "";
  sort_order: string;
};

const emptyStep = (): DraftStep => ({
  title: "",
  description: "",
  phase: "",
  sort_order: "",
});

const ClinicalChecklistTemplatesPage = () => {
  const { t } = useTranslation();
  const canManage = userHasAnyRole(["owner", "manager", "receptionist"]);

  document.title = t("settings.clinical_checklist.pageTitle");

  const [templates, setTemplates] = useState<ClinicalChecklistTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ClinicalChecklistTemplate | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<ClinicalChecklistTemplate | null>(
    null
  );
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [steps, setSteps] = useState<DraftStep[]>([emptyStep()]);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listClinicalChecklistTemplates({
        include_inactive: true,
      });
      setTemplates(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      toast.error(error?.data || t("settings.clinical_checklist.toast.loadError"));
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setIsDefault(false);
    setSteps([
      { title: "", description: "", phase: "hygiene", sort_order: "10" },
      { title: "", description: "", phase: "application", sort_order: "20" },
      { title: "", description: "", phase: "aftercare", sort_order: "30" },
    ]);
    setModalOpen(true);
  };

  const openEdit = (template: ClinicalChecklistTemplate) => {
    setEditing(template);
    setName(template.name);
    setDescription(template.description ?? "");
    setIsDefault(template.is_default);
    const draft =
      (template.steps ?? []).map((step, index) => ({
        title: step.title,
        description: step.description ?? "",
        phase: (step.phase ?? "") as ChecklistPhase | "",
        sort_order: String(step.sort_order ?? (index + 1) * 10),
      })) ?? [];
    setSteps(draft.length > 0 ? draft : [emptyStep()]);
    setModalOpen(true);
  };

  const updateStep = (index: number, patch: Partial<DraftStep>) => {
    setSteps((prev) =>
      prev.map((step, i) => (i === index ? { ...step, ...patch } : step))
    );
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(t("settings.clinical_checklist.validation.nameRequired"));
      return;
    }
    const cleaned = steps
      .map((step, index) => ({
        title: step.title.trim(),
        description: step.description.trim() || null,
        phase: step.phase || null,
        sort_order: step.sort_order ? Number(step.sort_order) : (index + 1) * 10,
      }))
      .filter((step) => step.title !== "");

    if (cleaned.length === 0) {
      toast.error(t("settings.clinical_checklist.validation.stepRequired"));
      return;
    }

    setSubmitting(true);
    try {
      if (editing) {
        await updateClinicalChecklistTemplate(editing.id, {
          name: name.trim(),
          description: description.trim() || null,
          is_default: isDefault,
          steps: cleaned,
        });
        toast.success(t("settings.clinical_checklist.toast.updated"));
      } else {
        await createClinicalChecklistTemplate({
          name: name.trim(),
          description: description.trim() || null,
          is_default: isDefault,
          steps: cleaned,
        });
        toast.success(t("settings.clinical_checklist.toast.created"));
      }
      setModalOpen(false);
      await fetchTemplates();
    } catch (error: any) {
      toast.error(error?.data || t("settings.clinical_checklist.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) {
      return;
    }
    setSubmitting(true);
    try {
      await deactivateClinicalChecklistTemplate(deactivateTarget.id);
      toast.success(t("settings.clinical_checklist.toast.deactivated"));
      setDeactivateTarget(null);
      await fetchTemplates();
    } catch (error: any) {
      toast.error(error?.data || t("settings.clinical_checklist.toast.deactivateError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async (template: ClinicalChecklistTemplate) => {
    setSubmitting(true);
    try {
      await updateClinicalChecklistTemplate(template.id, { is_active: true });
      toast.success(t("settings.clinical_checklist.toast.reactivated"));
      await fetchTemplates();
    } catch (error: any) {
      toast.error(error?.data || t("settings.clinical_checklist.toast.saveError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("settings.clinical_checklist.breadcrumb.title")}
          pageTitle={t("settings.clinical_checklist.breadcrumb.pageTitle")}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <h5 className="card-title mb-1">
                    {t("settings.clinical_checklist.card.title")}
                  </h5>
                  <p className="text-muted mb-0 small">
                    {t("settings.clinical_checklist.card.subtitle")}
                  </p>
                </div>
                {canManage ? (
                  <Button color="primary" onClick={openCreate}>
                    {t("settings.clinical_checklist.actions.new")}
                  </Button>
                ) : null}
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : templates.length === 0 ? (
                  <p className="text-muted mb-0">
                    {t("settings.clinical_checklist.empty")}
                  </p>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>{t("settings.clinical_checklist.table.name")}</th>
                          <th>{t("settings.clinical_checklist.table.steps")}</th>
                          <th>{t("settings.clinical_checklist.table.status")}</th>
                          <th className="text-end">
                            {t("settings.clinical_checklist.table.actions")}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {templates.map((template) => (
                          <tr key={template.id}>
                            <td>
                              {template.name}
                              {template.is_system ? (
                                <Badge color="soft-info" className="text-info ms-2">
                                  {t("settings.clinical_checklist.system")}
                                </Badge>
                              ) : null}
                              {template.is_default ? (
                                <Badge color="soft-success" className="text-success ms-2">
                                  {t("settings.clinical_checklist.default")}
                                </Badge>
                              ) : null}
                              <div className="small text-muted">
                                <code>{template.code}</code>
                              </div>
                            </td>
                            <td>{template.steps_count ?? template.steps?.length ?? 0}</td>
                            <td>
                              <Badge color={template.is_active ? "success" : "secondary"}>
                                {template.is_active
                                  ? t("settings.clinical_checklist.status.active")
                                  : t("settings.clinical_checklist.status.inactive")}
                              </Badge>
                            </td>
                            <td className="text-end">
                              {canManage ? (
                                <div className="d-inline-flex gap-1">
                                  <Button
                                    color="light"
                                    size="sm"
                                    onClick={() => openEdit(template)}
                                  >
                                    {t("settings.clinical_checklist.actions.edit")}
                                  </Button>
                                  {template.is_active ? (
                                    <Button
                                      color="soft-danger"
                                      size="sm"
                                      onClick={() => setDeactivateTarget(template)}
                                    >
                                      {t("settings.clinical_checklist.actions.deactivate")}
                                    </Button>
                                  ) : (
                                    <Button
                                      color="soft-success"
                                      size="sm"
                                      disabled={submitting}
                                      onClick={() => void handleReactivate(template)}
                                    >
                                      {t("settings.clinical_checklist.actions.reactivate")}
                                    </Button>
                                  )}
                                </div>
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          {editing
            ? t("settings.clinical_checklist.form.editTitle")
            : t("settings.clinical_checklist.form.createTitle")}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{t("settings.clinical_checklist.form.name")}</Label>
            <Input
              value={name}
              maxLength={160}
              onChange={(e) => setName(e.target.value)}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("settings.clinical_checklist.form.description")}</Label>
            <Input
              type="textarea"
              rows={2}
              value={description}
              maxLength={500}
              onChange={(e) => setDescription(e.target.value)}
            />
          </FormGroup>
          <FormGroup check className="mb-3">
            <Input
              id="checklist-is-default"
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            <Label check for="checklist-is-default">
              {t("settings.clinical_checklist.form.isDefault")}
            </Label>
          </FormGroup>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <Label className="mb-0">{t("settings.clinical_checklist.form.steps")}</Label>
            <Button
              color="light"
              size="sm"
              type="button"
              onClick={() => setSteps((prev) => [...prev, emptyStep()])}
            >
              {t("settings.clinical_checklist.actions.addStep")}
            </Button>
          </div>
          {steps.map((step, index) => (
            <div key={index} className="border rounded p-2 mb-2">
              <Row className="g-2">
                <Col md={6}>
                  <Input
                    placeholder={t("settings.clinical_checklist.form.stepTitle")}
                    value={step.title}
                    maxLength={160}
                    onChange={(e) => updateStep(index, { title: e.target.value })}
                  />
                </Col>
                <Col md={3}>
                  <Input
                    type="select"
                    value={step.phase}
                    onChange={(e) =>
                      updateStep(index, {
                        phase: e.target.value as ChecklistPhase | "",
                      })
                    }
                  >
                    <option value="">
                      {t("settings.clinical_checklist.form.phaseNone")}
                    </option>
                    <option value="hygiene">
                      {t("clients.protocol.checklist.phase.hygiene")}
                    </option>
                    <option value="application">
                      {t("clients.protocol.checklist.phase.application")}
                    </option>
                    <option value="aftercare">
                      {t("clients.protocol.checklist.phase.aftercare")}
                    </option>
                    <option value="other">
                      {t("clients.protocol.checklist.phase.other")}
                    </option>
                  </Input>
                </Col>
                <Col md={2}>
                  <Input
                    type="number"
                    placeholder={t("settings.clinical_checklist.form.sortOrder")}
                    value={step.sort_order}
                    onChange={(e) => updateStep(index, { sort_order: e.target.value })}
                  />
                </Col>
                <Col md={1} className="d-flex align-items-center">
                  <Button
                    color="link"
                    className="text-danger p-0"
                    type="button"
                    disabled={steps.length <= 1}
                    onClick={() =>
                      setSteps((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    ×
                  </Button>
                </Col>
                <Col xs={12}>
                  <Input
                    placeholder={t("settings.clinical_checklist.form.stepDescription")}
                    value={step.description}
                    maxLength={500}
                    onChange={(e) =>
                      updateStep(index, { description: e.target.value })
                    }
                  />
                </Col>
              </Row>
            </div>
          ))}
          <p className="text-muted small mb-0">
            {t("settings.clinical_checklist.form.stepsHint")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModalOpen(false)}>
            {t("settings.clinical_checklist.actions.cancel")}
          </Button>
          <Button color="primary" disabled={submitting} onClick={() => void handleSave()}>
            {t("settings.clinical_checklist.actions.save")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={deactivateTarget !== null}
        toggle={() => setDeactivateTarget(null)}
        centered
      >
        <ModalHeader toggle={() => setDeactivateTarget(null)}>
          {t("settings.clinical_checklist.deactivate.title")}
        </ModalHeader>
        <ModalBody>
          {t("settings.clinical_checklist.deactivate.description", {
            name: deactivateTarget?.name ?? "",
          })}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeactivateTarget(null)}>
            {t("settings.clinical_checklist.actions.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={submitting}
            onClick={() => void handleDeactivate()}
          >
            {t("settings.clinical_checklist.actions.confirmDeactivate")}
          </Button>
        </ModalFooter>
      </Modal>

      <ToastContainer />
    </div>
  );
};

export default ClinicalChecklistTemplatesPage;
